import {
  Alert,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import { DialogConfirm } from 'components'
import _ from 'lodash'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { postCreateUser, putEditUser } from 'services/apiKYE'
import { USER_ROLE } from 'utils/constants/application'
import { getUserPermissionIcon, USER_PERMISSION, USER_ROLE_DESCRIPTION } from 'utils/constants/user'
import { reportError, toastifyAxiosError } from 'utils/errorReporter'
import { ValidateEmail } from 'utils/validators'

const ConfigUserDialog = ({ open, userData, onClose, onSuccess }) => {
  const defaultBasicInfo = { name: '', email: '', enabled: true, user_role: USER_ROLE.compliance }
  const [permissions, setPermissions] = useState({})
  const [basicInfo, setBasicInfo] = useState(defaultBasicInfo)
  const [basicInfoValidation, setBasicInfoValidation] = useState({ name: false, email: false })
  const token = useSelector((state) => state.auth.access_token)

  const onConfirm = () => {
    if (_.isEmpty(userData)) {
      postCreateUser(token, { ...basicInfo, permissions })
        .then((res) => {
          toast.success(res)
          onClose()
        })
        .catch((res) => {
          toastifyAxiosError(res, 'Ocorreu um erro ao criar o usuário')
          reportError(res)
        })
    } else {
      putEditUser(token, {
        ...basicInfo,
        new_email: basicInfo.email !== userData.email ? basicInfo.email : null,
        email: userData.email,
        permissions,
      })
        .then((res) => {
          toast.success(res)
          onSuccess()
        })
        .catch((res) => {
          toastifyAxiosError(res, 'Ocorreu um erro ao alterar o usuário')
          reportError(res)
        })
    }
  }

  useEffect(() => {
    if (!_.isEmpty(userData)) {
      setBasicInfo({ ...userData })
      setPermissions(userData.permissions)
    }
    else {
      setBasicInfo(defaultBasicInfo)
      setPermissions({})
    }
  }, [userData])

  useEffect(() => {
    setBasicInfoValidation({  email: ValidateEmail(basicInfo.email),  name: basicInfo.name?.length > 5 })
  }, [basicInfo])

  const textFieldHasError = (fieldName) => {
    const isInvalid = basicInfoValidation[fieldName] === false
    return !_.isEmpty(basicInfo[fieldName]) && isInvalid
  }


  return (
    <DialogConfirm
      customFullWidth={true}
      open={open}
      title={_.isEmpty(userData) ? 'Criar usuário' : 'Alterar usuário'}
      btnConfirmText={_.isEmpty(userData) ? 'Criar usuário' : 'Salvar alterações'}
      btnCancelText="Cancelar"
      onConfirm={onConfirm}
      onCancel={onClose}
      btnConfirmDisabled={_.some(basicInfoValidation, field => !field)}
    >
      <h4 style={{ fontWeight: 550, color: '#222' }}>Informações básicas</h4>

      <div style={{ marginTop: 10, display: 'flex', gap: 10, justifyContent: 'center' }}>
        <TextField
          variant="standard"
          label="Nome"
          placeholder="Nome"
          fullWidth={true}
          error={textFieldHasError('name')}
          helperText={textFieldHasError('name') && 'Nome inválido'}
          value={basicInfo.name}
          onChange={(ev) => setBasicInfo({ ...basicInfo, name: ev.target.value })}
        ></TextField>

        <TextField
          variant="standard"
          label="Email"
          placeholder="exemplo@email.com"
          fullWidth={true}
          error={textFieldHasError('email')}
          helperText={textFieldHasError('email') && 'E-mail inválido'}
          value={basicInfo.email}
          onChange={(ev) => setBasicInfo({ ...basicInfo, email: ev.target.value })}
        ></TextField>
      </div>

      {_.isEmpty(userData) && (
        <div style={{ margin: '20px 0' }}>
          <Alert severity="info" color="primary">
            Ao criar um usuário, o sistema enviará um email com instruções para o primeiro acesso.
          </Alert>
        </div>
      )}

      {!_.isEmpty(userData) && (
        <div style={{ margin: '20px 0' }}>
          <FormControlLabel
            label="Usuário Ativo"
            control={
              <Checkbox
                checked={basicInfo.enabled}
                onChange={() => setBasicInfo({ ...basicInfo, enabled: !basicInfo.enabled })}
              />
            }
          />
        </div>
      )}

      <div style={{ margin: '20px 0' }}>
        <FormControl style={{ width: 300 }}>
          <InputLabel variant="standard" htmlFor="user-role">
            Função
          </InputLabel>
          <Select
            labelId="user-role"
            id="user-role"
            label="Função"
            variant="standard"
            value={basicInfo.user_role}
            onChange={(ev) => setBasicInfo({ ...basicInfo, user_role: ev.target.value })}
            fullWidth={true}
          >
            {_.map(USER_ROLE, (role) => (
              <MenuItem key={role} value={role}>
                {role}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <div style={{ margin: '10px 0' }}>{USER_ROLE_DESCRIPTION[basicInfo.user_role]}</div>

        <div style={{ margin: '30px 0' }}>
          <Divider></Divider>
        </div>

        <h4 style={{ fontWeight: 550, color: '#222' }}>Permissões adicionais</h4>

        <div>
          {_.map(USER_PERMISSION, (val, key) => (
            <FormControlLabel
              key={key}
              label={<span style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                {getUserPermissionIcon(key)}
                <span>{val}</span>
                </span>}
              control={
                <Checkbox
                  checked={permissions[key.toLowerCase()] || false}
                  onChange={() => setPermissions({ ...permissions, [key.toLowerCase()]: !permissions[key.toLowerCase()] })}
                />
              }
            />
          ))}

          <div style={{ marginTop: 10 }}>
            <Alert severity="warning" color="primary">
              Tenha cuidado ao conceder permissões extras. Apenas usuários confiáveis devem ter acesso a esses
              privilégios.
            </Alert>
          </div>
        </div>
      </div>
    </DialogConfirm>
  )
}

export default ConfigUserDialog
